import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Line } from 'react-chartjs-2';
import {
  getMember,
  getMemberCalendar,
  getMemberReport,
  logoutMember,
  markMemberWhatsappStarted,
  saveMemberProfile,
  sendMemberChat,
  startStripeBillingPortal,
  startStripeCheckout
} from '../services/api';
import { localizeChineseText } from '../services/chineseText';
import UsernameSettings from './UsernameSettings';

export const labels = {
  'zh-TW': {
    title: '私人風水顧問會員區',
    intro: '登入後保存命盤資料。訂閱後，可每日向玄澤大師追問風水、流年與行事時機。',
    login: '使用 Google 登入',
    logout: '登出',
    loading: '正在讀取會員資料...',
    profile: '個人資料',
    report: '一生命評',
    calendar: '三十日通勝',
    chat: '玄澤大師問答',
    subscribe: '訂閱全年顧問服務',
    manageSubscription: '管理／取消訂閱',
    whatsappReminderTitle: '開始 WhatsApp 私人對話',
    whatsappReminderCopy: '你已開通全年顧問服務。若尚未開始 WhatsApp 對話，請用你登記的手機號碼傳送訊息給玄澤師傅，師傅即可核對會員身份並開始每日玄學顧問。',
    whatsappStart: 'WhatsApp 聯絡玄澤師傅',
    whatsappSent: '我已發送 WhatsApp 訊息',
    whatsappMarked: '已記錄你完成 WhatsApp 開場訊息。',
    active: '顧問服務已開通',
    inactive: '尚未訂閱全年顧問服務',
    save: '儲存資料',
    saving: '儲存中...',
    name: '姓名',
    gender: '性別',
    male: '男',
    female: '女',
    other: '其他',
    dob: '出生日期',
    time: '出生時間',
    place: '出生地點',
    father: '父親姓名（可選）',
    email: '電郵',
    whatsapp: 'WhatsApp',
    profileSaved: '資料已儲存。',
    profileFirst: '請先完成個人資料。',
    chartTitle: '大運起伏',
    annualTitle: '近年流年',
    comprehensiveTitle: '一生命評總覽',
    nameReading: '姓名吉凶',
    nameAiReading: '姓名詳解',
    recentLuck: '近運提示',
    currentLuckCycle: '當前大運',
    currentYear: '今年',
    currentMonth: '近月',
    currentWeek: '未來七日',
    today: '今日',
    recommendedJobs: '適合發展',
    lifeSections: '分項命評',
    noScore: '待審',
    askPlaceholder: '輸入你想問玄澤大師的問題...',
    send: '送出',
    subscribeFirst: '訂閱後即可使用會員問答。',
    checkoutSuccess: '付款完成。Stripe 正在通知系統開通顧問服務，若狀態未即時更新，請稍等一分鐘再刷新。',
    checkoutCancelled: '付款未完成，你可以稍後再訂閱。',
    billingReturn: '你已返回會員區。訂閱更新會由 Stripe 通知系統，若狀態未即時更新，請稍後刷新。',
    billingUnavailable: '暫時未能開啟訂閱管理。請確認已完成訂閱，或稍後再試。',
    should: '宜',
    avoid: '忌'
  },
  'zh-CN': {
    title: '私人风水顾问会员区',
    intro: '登录后保存命盘资料。订阅后，可每日向玄泽大师追问风水、流年与行事时机。',
    login: '使用 Google 登录',
    logout: '登出',
    loading: '正在读取会员资料...',
    profile: '个人资料',
    report: '一生命评',
    calendar: '三十日通胜',
    chat: '玄泽大师问答',
    subscribe: '订阅全年顾问服务',
    manageSubscription: '管理／取消订阅',
    whatsappReminderTitle: '开始 WhatsApp 私人对话',
    whatsappReminderCopy: '你已开通全年顾问服务。若尚未开始 WhatsApp 对话，请用你登记的手机号传送讯息给玄泽师傅，师傅即可核对会员身份并开始每日玄学顾问。',
    whatsappStart: 'WhatsApp 联络玄泽师傅',
    whatsappSent: '我已发送 WhatsApp 讯息',
    whatsappMarked: '已记录你完成 WhatsApp 开场讯息。',
    active: '顾问服务已开通',
    inactive: '尚未订阅全年顾问服务',
    save: '储存资料',
    saving: '储存中...',
    name: '姓名',
    gender: '性别',
    male: '男',
    female: '女',
    other: '其他',
    dob: '出生日期',
    time: '出生时间',
    place: '出生地点',
    father: '父亲姓名（可选）',
    email: '电邮',
    whatsapp: 'WhatsApp',
    profileSaved: '资料已储存。',
    profileFirst: '请先完成个人资料。',
    chartTitle: '大运起伏',
    annualTitle: '近年流年',
    comprehensiveTitle: '一生命评总览',
    nameReading: '姓名吉凶',
    nameAiReading: '姓名详解',
    recentLuck: '近运提示',
    currentLuckCycle: '当前大运',
    currentYear: '今年',
    currentMonth: '近月',
    currentWeek: '未来七日',
    today: '今日',
    recommendedJobs: '适合发展',
    lifeSections: '分项命评',
    noScore: '待审',
    askPlaceholder: '输入你想问玄泽大师的问题...',
    send: '送出',
    subscribeFirst: '订阅后即可使用会员问答。',
    checkoutSuccess: '付款完成。Stripe 正在通知系统开通顾问服务，若状态未即时更新，请稍等一分钟再刷新。',
    checkoutCancelled: '付款未完成，你可以稍后再订阅。',
    billingReturn: '你已返回会员区。订阅更新会由 Stripe 通知系统，若状态未即时更新，请稍后刷新。',
    billingUnavailable: '暂时未能开启订阅管理。请确认已完成订阅，或稍后再试。',
    should: '宜',
    avoid: '忌'
  },
  en: {
    title: 'Private Feng Shui Member Area',
    intro: 'Save your birth profile after Google login. Subscribers can ask Master Xuanze about feng shui, annual timing and daily decisions.',
    login: 'Continue with Google',
    logout: 'Logout',
    loading: 'Loading member profile...',
    profile: 'Profile',
    report: 'Life report',
    calendar: '30-day calendar',
    chat: 'Ask Master Xuanze',
    subscribe: 'Subscribe Annual Advisory Services',
    manageSubscription: 'Manage / cancel subscription',
    whatsappReminderTitle: 'Start your private WhatsApp chat',
    whatsappReminderCopy: 'Your annual advisory service is active. If you have not started WhatsApp yet, send Master Xuanze a message from your registered mobile number so your membership can be matched before daily advisory begins.',
    whatsappStart: 'Message Master Xuanze on WhatsApp',
    whatsappSent: 'I have sent the WhatsApp message',
    whatsappMarked: 'Your WhatsApp opening message has been marked as sent.',
    active: 'Advisory service active',
    inactive: 'Annual advisory not subscribed yet',
    save: 'Save profile',
    saving: 'Saving...',
    name: 'Name',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    dob: 'Date of birth',
    time: 'Time of birth',
    place: 'Place of birth',
    father: 'Father name (optional)',
    email: 'Email',
    whatsapp: 'WhatsApp',
    profileSaved: 'Profile saved.',
    profileFirst: 'Please complete your profile first.',
    chartTitle: 'Luck-cycle movement',
    annualTitle: 'Recent annual fortune',
    comprehensiveTitle: 'Full life report',
    nameReading: 'Name fortune',
    nameAiReading: 'Name interpretation',
    recentLuck: 'Recent luck',
    currentLuckCycle: 'Current 10-year cycle',
    currentYear: 'This year',
    currentMonth: 'Recent month',
    currentWeek: 'Next seven days',
    today: 'Today',
    recommendedJobs: 'Suitable directions',
    lifeSections: 'Detailed reading',
    noScore: 'Pending',
    askPlaceholder: 'Ask Master Xuanze a question...',
    send: 'Send',
    subscribeFirst: 'Subscribe to use member chat.',
    checkoutSuccess: 'Payment completed. Stripe is notifying the system to activate advisory access; if the status is not updated immediately, refresh again in about a minute.',
    checkoutCancelled: 'Payment was not completed. You can subscribe again later.',
    billingReturn: 'You are back in the member area. Stripe will notify the system of subscription updates; refresh shortly if the status is not updated yet.',
    billingUnavailable: 'Subscription management is not available yet. Please make sure your subscription is active, or try again shortly.',
    should: 'Good for',
    avoid: 'Avoid'
  },
  ja: {
    title: '個人風水顧問 会員エリア', intro: 'Google ログイン後に生年月日プロフィールを保存できます。購読者は玄澤先生に風水、年運、日々の判断を相談できます。', login: 'Google でログイン', logout: 'ログアウト', loading: '会員情報を読み込んでいます...', profile: 'プロフィール', report: '人生鑑定', calendar: '30日通勝', chat: '玄澤先生に相談', subscribe: '年間顧問サービスを購読', manageSubscription: '購読を管理・解約', whatsappReminderTitle: 'WhatsApp の個別相談を始める', whatsappReminderCopy: '年間顧問サービスは有効です。まだ WhatsApp での相談を始めていない場合は、登録済みの電話番号から玄澤先生へメッセージをお送りください。会員情報を照合後、毎日の顧問サービスを開始します。', whatsappStart: 'WhatsApp で玄澤先生に連絡', whatsappSent: 'WhatsApp メッセージを送信しました', whatsappMarked: 'WhatsApp の開始メッセージを記録しました。', active: '顧問サービスは有効です', inactive: '年間顧問サービスは未購読です', save: '保存', saving: '保存中...', name: '氏名', gender: '性別', male: '男性', female: '女性', other: 'その他', dob: '生年月日', time: '出生時刻', place: '出生地', father: '父親の氏名（任意）', email: 'メールアドレス', whatsapp: 'WhatsApp', profileSaved: 'プロフィールを保存しました。', profileFirst: '先にプロフィールを入力してください。', chartTitle: '運気サイクル', annualTitle: '最近の年運', comprehensiveTitle: '人生鑑定の概要', nameReading: '姓名判断', nameAiReading: '姓名の解説', recentLuck: '最近の運気', currentLuckCycle: '現在の10年運', currentYear: '今年', currentMonth: '今月', currentWeek: '今後7日間', today: '今日', recommendedJobs: '適した方向', lifeSections: '項目別鑑定', noScore: '確認待ち', askPlaceholder: '玄澤先生への質問を入力してください...', send: '送信', subscribeFirst: '会員相談を利用するには購読してください。', checkoutSuccess: '支払いが完了しました。Stripe が顧問サービスの有効化を通知しています。すぐに反映されない場合は、1分ほど待ってから更新してください。', checkoutCancelled: '支払いは完了しませんでした。後で再度購読できます。', billingReturn: '会員エリアに戻りました。購読内容の更新は Stripe から通知されます。すぐに反映されない場合は、しばらくしてから更新してください。', billingUnavailable: '購読管理を開けませんでした。購読が完了しているか確認するか、後でもう一度お試しください。', should: 'おすすめ', avoid: '避ける'
  },
  ko: {
    title: '개인 풍수 자문 회원 공간', intro: 'Google 로그인 후 출생 프로필을 저장하세요. 구독 회원은 현택 선생님께 풍수, 연운 및 일상의 판단을 상담할 수 있습니다.', login: 'Google로 로그인', logout: '로그아웃', loading: '회원 정보를 불러오는 중...', profile: '프로필', report: '평생 운세', calendar: '30일 길일 달력', chat: '현택 선생님께 질문', subscribe: '연간 자문 서비스 구독', manageSubscription: '구독 관리 / 취소', whatsappReminderTitle: 'WhatsApp 개인 상담 시작', whatsappReminderCopy: '연간 자문 서비스가 활성화되었습니다. 아직 WhatsApp 상담을 시작하지 않았다면, 등록한 전화번호로 현택 선생님께 메시지를 보내 주세요. 회원 정보를 확인한 뒤 매일 자문을 시작합니다.', whatsappStart: 'WhatsApp으로 현택 선생님께 연락', whatsappSent: 'WhatsApp 메시지를 보냈습니다', whatsappMarked: 'WhatsApp 시작 메시지를 기록했습니다.', active: '자문 서비스 활성화됨', inactive: '연간 자문 서비스를 아직 구독하지 않았습니다', save: '저장', saving: '저장 중...', name: '이름', gender: '성별', male: '남성', female: '여성', other: '기타', dob: '생년월일', time: '출생 시간', place: '출생지', father: '부친 성함(선택)', email: '이메일', whatsapp: 'WhatsApp', profileSaved: '프로필을 저장했습니다.', profileFirst: '먼저 프로필을 완성해 주세요.', chartTitle: '대운 흐름', annualTitle: '최근 세운', comprehensiveTitle: '평생 운세 개요', nameReading: '성명 운세', nameAiReading: '이름 풀이', recentLuck: '최근 운세', currentLuckCycle: '현재 10년 대운', currentYear: '올해', currentMonth: '이번 달', currentWeek: '향후 7일', today: '오늘', recommendedJobs: '적합한 방향', lifeSections: '항목별 운세', noScore: '검토 대기', askPlaceholder: '현택 선생님께 질문을 입력하세요...', send: '보내기', subscribeFirst: '회원 상담을 이용하려면 구독해 주세요.', checkoutSuccess: '결제가 완료되었습니다. Stripe가 자문 서비스 활성화를 알리고 있습니다. 상태가 바로 갱신되지 않으면 1분 후 새로고침해 주세요.', checkoutCancelled: '결제가 완료되지 않았습니다. 나중에 다시 구독할 수 있습니다.', billingReturn: '회원 공간으로 돌아왔습니다. 구독 변경 사항은 Stripe가 시스템에 알립니다. 상태가 바로 갱신되지 않으면 잠시 후 새로고침해 주세요.', billingUnavailable: '구독 관리를 열 수 없습니다. 구독 완료 여부를 확인하거나 잠시 후 다시 시도해 주세요.', should: '권장', avoid: '피하기'
  },
  ms: {
    title: 'Ruang Ahli Perunding Feng Shui Peribadi', intro: 'Simpan profil kelahiran anda selepas log masuk Google. Pelanggan boleh bertanya kepada Master Xuanze tentang feng shui, masa tahunan dan keputusan harian.', login: 'Log masuk dengan Google', logout: 'Log keluar', loading: 'Memuatkan maklumat ahli...', profile: 'Profil', report: 'Laporan kehidupan', calendar: 'Kalendar 30 hari', chat: 'Tanya Master Xuanze', subscribe: 'Langgan Perkhidmatan Nasihat Tahunan', manageSubscription: 'Urus / batalkan langganan', whatsappReminderTitle: 'Mulakan perbualan WhatsApp peribadi anda', whatsappReminderCopy: 'Perkhidmatan nasihat tahunan anda aktif. Jika anda belum memulakan perbualan WhatsApp, hantarkan mesej kepada Master Xuanze menggunakan nombor telefon berdaftar supaya keahlian anda dapat disahkan sebelum nasihat harian bermula.', whatsappStart: 'Hubungi Master Xuanze di WhatsApp', whatsappSent: 'Saya telah menghantar mesej WhatsApp', whatsappMarked: 'Mesej pembukaan WhatsApp anda telah direkodkan.', active: 'Perkhidmatan nasihat aktif', inactive: 'Perkhidmatan nasihat tahunan belum dilanggan', save: 'Simpan', saving: 'Menyimpan...', name: 'Nama', gender: 'Jantina', male: 'Lelaki', female: 'Perempuan', other: 'Lain-lain', dob: 'Tarikh lahir', time: 'Masa lahir', place: 'Tempat lahir', father: 'Nama bapa (pilihan)', email: 'E-mel', whatsapp: 'WhatsApp', profileSaved: 'Profil disimpan.', profileFirst: 'Sila lengkapkan profil anda dahulu.', chartTitle: 'Pergerakan kitaran nasib', annualTitle: 'Nasib tahunan terkini', comprehensiveTitle: 'Gambaran laporan kehidupan', nameReading: 'Nasib nama', nameAiReading: 'Huraian nama', recentLuck: 'Nasib terkini', currentLuckCycle: 'Kitaran 10 tahun semasa', currentYear: 'Tahun ini', currentMonth: 'Bulan ini', currentWeek: 'Tujuh hari akan datang', today: 'Hari ini', recommendedJobs: 'Arah yang sesuai', lifeSections: 'Bacaan terperinci', noScore: 'Menunggu semakan', askPlaceholder: 'Tanya soalan kepada Master Xuanze...', send: 'Hantar', subscribeFirst: 'Langgan untuk menggunakan perbualan ahli.', checkoutSuccess: 'Bayaran selesai. Stripe sedang memaklumkan sistem untuk mengaktifkan akses nasihat; jika status belum dikemas kini, muat semula selepas kira-kira satu minit.', checkoutCancelled: 'Bayaran tidak selesai. Anda boleh melanggan semula kemudian.', billingReturn: 'Anda telah kembali ke ruang ahli. Stripe akan memaklumkan sistem tentang kemas kini langganan; muat semula sebentar lagi jika status belum berubah.', billingUnavailable: 'Pengurusan langganan belum dapat dibuka. Pastikan langganan anda aktif atau cuba lagi sebentar lagi.', should: 'Sesuai untuk', avoid: 'Elakkan'
  },
  es: {
    title: 'Área de Miembros de Feng Shui Privado', intro: 'Guarda tu perfil de nacimiento tras iniciar sesión con Google. Los suscriptores pueden consultar al Maestro Xuanze sobre feng shui, ritmos anuales y decisiones diarias.', login: 'Continuar con Google', logout: 'Cerrar sesión', loading: 'Cargando información de miembro...', profile: 'Perfil', report: 'Informe de vida', calendar: 'Calendario de 30 días', chat: 'Preguntar al Maestro Xuanze', subscribe: 'Suscribirse al servicio anual de asesoría', manageSubscription: 'Gestionar / cancelar suscripción', whatsappReminderTitle: 'Inicia tu chat privado de WhatsApp', whatsappReminderCopy: 'Tu servicio anual de asesoría está activo. Si todavía no has iniciado el chat por WhatsApp, envía un mensaje al Maestro Xuanze desde tu número registrado para que se pueda verificar tu membresía antes de iniciar la asesoría diaria.', whatsappStart: 'Escribir al Maestro Xuanze por WhatsApp', whatsappSent: 'He enviado el mensaje de WhatsApp', whatsappMarked: 'Se ha registrado tu mensaje inicial de WhatsApp.', active: 'Servicio de asesoría activo', inactive: 'El servicio anual de asesoría aún no está suscrito', save: 'Guardar', saving: 'Guardando...', name: 'Nombre', gender: 'Género', male: 'Hombre', female: 'Mujer', other: 'Otro', dob: 'Fecha de nacimiento', time: 'Hora de nacimiento', place: 'Lugar de nacimiento', father: 'Nombre del padre (opcional)', email: 'Correo electrónico', whatsapp: 'WhatsApp', profileSaved: 'Perfil guardado.', profileFirst: 'Completa primero tu perfil.', chartTitle: 'Movimiento del ciclo de fortuna', annualTitle: 'Fortuna anual reciente', comprehensiveTitle: 'Resumen del informe de vida', nameReading: 'Fortuna del nombre', nameAiReading: 'Interpretación del nombre', recentLuck: 'Fortuna reciente', currentLuckCycle: 'Ciclo actual de 10 años', currentYear: 'Este año', currentMonth: 'Este mes', currentWeek: 'Próximos siete días', today: 'Hoy', recommendedJobs: 'Direcciones adecuadas', lifeSections: 'Lectura detallada', noScore: 'Pendiente', askPlaceholder: 'Haz una pregunta al Maestro Xuanze...', send: 'Enviar', subscribeFirst: 'Suscríbete para usar el chat de miembros.', checkoutSuccess: 'Pago completado. Stripe está notificando al sistema para activar el acceso de asesoría; si el estado no se actualiza de inmediato, vuelve a cargar en aproximadamente un minuto.', checkoutCancelled: 'El pago no se completó. Puedes suscribirte de nuevo más tarde.', billingReturn: 'Has vuelto al área de miembros. Stripe notificará al sistema las actualizaciones de suscripción; vuelve a cargar pronto si el estado no se actualiza.', billingUnavailable: 'No se puede abrir la gestión de suscripción. Confirma que tu suscripción esté activa o inténtalo de nuevo en breve.', should: 'Bueno para', avoid: 'Evitar'
  }
};

const emptyProfile = {
  name: '',
  gender: 'male',
  dob: '',
  birth_time: '08:00',
  birth_place: '',
  father_name: '',
  email: '',
  whatsapp: ''
};

const MemberPage = ({ lang = 'zh-TW' }) => {
  const copy = labels[lang] || labels['zh-TW'];
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [activeTab, setActiveTab] = useState('profile');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');

  const txt = useCallback((value) => localizeChineseText(value, lang), [lang]);

  useEffect(() => {
    const checkoutStatus = new URLSearchParams(window.location.search).get('checkout');
    if (checkoutStatus === 'success') {
      setStatus(copy.checkoutSuccess);
      setActiveTab('report');
    } else if (checkoutStatus === 'cancelled') {
      setStatus(copy.checkoutCancelled);
    }
    if (new URLSearchParams(window.location.search).get('billing') === 'return') {
      setStatus(copy.billingReturn);
      setActiveTab('profile');
    }
    getMember().then((data) => {
      setMe(data);
      if (data?.member) {
        setProfile({
          ...emptyProfile,
          name: data.member.profile?.name || data.member.name || '',
          email: data.member.profile?.email || data.member.email || '',
          gender: data.member.profile?.gender || 'male',
          dob: data.member.profile?.dob || '',
          birth_time: data.member.profile?.birth_time || '08:00',
          birth_place: data.member.profile?.birth_place || '',
          father_name: data.member.profile?.father_name || '',
          whatsapp: data.member.profile?.whatsapp || ''
        });
      }
    }).catch(() => setMe({ authenticated: false }));
  }, [copy.billingReturn, copy.checkoutCancelled, copy.checkoutSuccess]);

  const chartData = useMemo(() => ({
    labels: report?.charts?.luck_scores?.map((point) => point.label) || [],
    datasets: [
      {
        label: copy.chartTitle,
        data: report?.charts?.luck_scores?.map((point) => point.score) || [],
        borderColor: '#b86a12',
        backgroundColor: 'rgba(184, 106, 18, 0.16)',
        tension: 0.35,
        fill: true
      }
    ]
  }), [report, copy.chartTitle]);

  const loadReport = async () => {
    setBusy(true);
    setStatus('');
    try {
      setReport(await getMemberReport(lang));
    } catch (error) {
      setStatus(error?.response?.data?.detail || copy.profileFirst);
    } finally {
      setBusy(false);
    }
  };

  const loadCalendar = async () => {
    setBusy(true);
    setStatus('');
    try {
      setCalendar(await getMemberCalendar(lang));
    } catch (error) {
      setStatus(error?.response?.data?.detail || copy.profileFirst);
    } finally {
      setBusy(false);
    }
  };

  const handleTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'report' && !report) loadReport();
    if (tab === 'calendar' && !calendar) loadCalendar();
  };

  useEffect(() => {
    if (me?.authenticated && activeTab === 'report' && !report && !busy) {
      loadReport();
    }
    if (me?.authenticated && activeTab === 'calendar' && !calendar && !busy) {
      loadCalendar();
    }
  }, [me, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (event) => {
    event.preventDefault();
    setBusy(true);
    setStatus('');
    try {
      const saved = await saveMemberProfile({ ...profile, lang });
      setMe((current) => ({ ...(current || {}), authenticated: true, member: saved.member }));
      setStatus(copy.profileSaved);
    } catch (error) {
      setStatus(error?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    setBusy(true);
    setStatus('');
    try {
      const result = await startStripeCheckout();
      window.location.href = result.url;
    } catch (error) {
      setStatus(error?.response?.data?.detail || 'Stripe checkout is not configured yet.');
    } finally {
      setBusy(false);
    }
  };

  const handleManageSubscription = async () => {
    setBusy(true);
    setStatus('');
    try {
      const result = await startStripeBillingPortal();
      window.location.href = result.url;
    } catch (error) {
      setStatus(error?.response?.data?.detail || copy.billingUnavailable);
    } finally {
      setBusy(false);
    }
  };

  const handleWhatsappSent = async () => {
    setBusy(true);
    setStatus('');
    try {
      const result = await markMemberWhatsappStarted();
      setMe((current) => ({ ...(current || {}), member: result.member }));
      setStatus(copy.whatsappMarked);
    } catch (error) {
      setStatus(error?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logoutMember();
    window.location.href = '/private-advisory.html?logout=success';
  };

  const sendChat = async () => {
    const message = chatInput.trim();
    if (!message || busy) return;
    setChatInput('');
    const nextMessages = [...messages, { role: 'user', content: message }];
    setMessages(nextMessages);
    setBusy(true);
    try {
      const answer = await sendMemberChat({ message, chat_history: messages, lang });
      setMessages([...nextMessages, { role: 'assistant', content: answer.response }]);
    } catch (error) {
      setMessages([...nextMessages, { role: 'assistant', content: error?.response?.data?.detail || copy.subscribeFirst }]);
    } finally {
      setBusy(false);
    }
  };

  if (!me) {
    return <main className="bz-main"><section className="member-panel">{copy.loading}</section></main>;
  }

  if (!me.authenticated) {
    return (
      <main className="bz-main">
        <section className="member-panel member-panel--center">
          <span className="fc-eyebrow">{copy.profile}</span>
          <h1>{copy.title}</h1>
          <p>{copy.intro}</p>
          <a className="fc-btn fc-btn--primary fc-btn--lg" href="/bazi/api/auth/google/login">{copy.login}</a>
        </section>
      </main>
    );
  }

  const member = me.member;
  const isActive = me.subscription_active;
  const whatsappStarted = Boolean(member.profile?.whatsapp_started);
  const registeredWhatsapp = profile.whatsapp || member.profile?.whatsapp || '';
  const whatsappMessage = [
    '我是 Seeable.Life 會員，我已完成註冊及付款。',
    registeredWhatsapp ? `我的登記 WhatsApp 號碼是：${registeredWhatsapp}。` : '',
    '請玄澤師傅為我開啟私人風水顧問對話。'
  ].filter(Boolean).join(' ');
  const whatsappUrl = `https://wa.me/85263344341?text=${encodeURIComponent(whatsappMessage)}`;
  const years = report?.fortune?.annual_fortunes?.years || [];
  const lifeReport = report?.life_report;
  const recent = lifeReport?.recent || {};
  const chengguSong = report?.fortune?.chenggu?.song || {};
  const chengguText = typeof chengguSong === 'object'
    ? [chengguSong.verse, chengguSong.annotation, chengguSong.summary].filter(Boolean).join(' ')
    : (chengguSong || report?.fortune?.chenggu?.summary || '');
  const scoreText = (score) => (score === null || score === undefined || score === '' ? copy.noScore : `${score}/100`);

  return (
    <main className="bz-main member-page">
      <section className="member-hero">
        <div>
          <span className="fc-eyebrow">{copy.active}</span>
          <h1>{copy.title}</h1>
          <p>{copy.intro}</p>
        </div>
        <div className="member-account-card">
          {member.picture && <img src={member.picture} alt="" />}
          <strong>{member.name}</strong>
          <span>{member.email}</span>
          <em>{isActive ? copy.active : copy.inactive}</em>
          {isActive && (
            <button type="button" className="fc-btn fc-btn--secondary fc-btn--sm" onClick={handleManageSubscription} disabled={busy}>
              {copy.manageSubscription}
            </button>
          )}
          <button type="button" className="fc-btn fc-btn--ghost fc-btn--sm" onClick={handleLogout}>{copy.logout}</button>
        </div>
      </section>

      <nav className="member-tabs" aria-label="Member area">
        {['profile', 'report', 'calendar', 'chat'].map((tab) => (
          <button key={tab} type="button" className={activeTab === tab ? 'active' : ''} onClick={() => handleTab(tab)}>
            {copy[tab]}
          </button>
        ))}
      </nav>

      {status && <div className="fc-error" role="status">{status}</div>}

      {isActive && !whatsappStarted && (
        <section className="member-whatsapp-reminder" aria-label={copy.whatsappReminderTitle}>
          <div>
            <span className="fc-eyebrow">WhatsApp</span>
            <h2>{copy.whatsappReminderTitle}</h2>
            <p>{copy.whatsappReminderCopy}</p>
            {registeredWhatsapp && <p className="member-whatsapp-phone">{copy.whatsapp}: {registeredWhatsapp}</p>}
          </div>
          <div className="member-whatsapp-actions">
            <a className="fc-btn fc-btn--primary fc-btn--lg" href={whatsappUrl} target="_blank" rel="noreferrer">
              {copy.whatsappStart}
            </a>
            <button type="button" className="fc-btn fc-btn--ghost fc-btn--lg" onClick={handleWhatsappSent} disabled={busy}>
              {copy.whatsappSent}
            </button>
          </div>
        </section>
      )}

      {activeTab === 'profile' && <UsernameSettings lang={lang} />}
      {activeTab === 'profile' && (
        <form className="member-panel member-form" onSubmit={handleSave}>
          <div className="fc-field"><label className="fc-label">{copy.name}</label><input className="fc-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required /></div>
          <div className="fc-field"><label className="fc-label">{copy.gender}</label><select className="fc-select" value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })}><option value="male">{copy.male}</option><option value="female">{copy.female}</option><option value="other">{copy.other}</option></select></div>
          <div className="fc-field"><label className="fc-label">{copy.dob}</label><input className="fc-input" type="date" value={profile.dob} onChange={(e) => setProfile({ ...profile, dob: e.target.value })} required /></div>
          <div className="fc-field"><label className="fc-label">{copy.time}</label><input className="fc-input" type="time" value={profile.birth_time} onChange={(e) => setProfile({ ...profile, birth_time: e.target.value })} required /></div>
          <div className="fc-field"><label className="fc-label">{copy.place}</label><input className="fc-input" value={profile.birth_place} onChange={(e) => setProfile({ ...profile, birth_place: e.target.value })} required /></div>
          <div className="fc-field"><label className="fc-label">{copy.father}</label><input className="fc-input" value={profile.father_name} onChange={(e) => setProfile({ ...profile, father_name: e.target.value })} /></div>
          <div className="fc-field"><label className="fc-label">{copy.email}</label><input className="fc-input" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} required /></div>
          <div className="fc-field"><label className="fc-label">{copy.whatsapp}</label><input className="fc-input" value={profile.whatsapp} onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })} required /></div>
          <div className="member-actions">
            <button className="fc-btn fc-btn--primary fc-btn--lg" type="submit" disabled={busy}>{busy ? copy.saving : copy.save}</button>
            {isActive ? (
              <button className="fc-btn fc-btn--ghost fc-btn--lg" type="button" onClick={handleManageSubscription} disabled={busy}>
                {copy.manageSubscription}
              </button>
            ) : (
              <button className="fc-btn fc-btn--secondary fc-btn--lg" type="button" onClick={handleCheckout} disabled={busy}>{copy.subscribe}</button>
            )}
          </div>
        </form>
      )}

      {activeTab === 'report' && (
        <section className="member-panel">
          {busy && <p>{copy.loading}</p>}
          {report && (
            <>
              <div className="member-summary-grid">
                <article><span>{copy.name}</span><strong>{txt(report.fortune.profile?.name)}</strong></article>
                <article><span>四柱</span><strong>{Object.values(report.fortune.bazi?.pillars || {}).join(' ')}</strong></article>
                <article><span>鬼谷</span><strong>{txt(report.fortune.method?.symbol_heads)} · {txt(report.fortune.method?.matching_key)}</strong></article>
                <article><span>稱骨</span><strong>{txt(report.fortune.chenggu?.total_display || '')}</strong></article>
              </div>

              {lifeReport && (
                <div className="member-life-report">
                  <article className="member-report-hero">
                    <span className="fc-eyebrow">{copy.comprehensiveTitle}</span>
                    <h2>{txt(lifeReport.headline)}</h2>
                    <p>{txt(lifeReport.overview)}</p>
                    <div className="member-tag-row">
                      {(lifeReport.personality_tags || []).map((tag) => <span key={tag}>{txt(tag)}</span>)}
                    </div>
                  </article>

                  <div className="member-insight-grid">
                    <article className="member-insight-card member-insight-card--name">
                      <span>{copy.nameReading}</span>
                      <strong>{scoreText(lifeReport.name_reading?.score)} · {txt(lifeReport.name_reading?.grade)}</strong>
                      <p>{txt(lifeReport.name_reading?.summary)}</p>
                      <p>{txt(lifeReport.name_reading?.advice)}</p>
                      {lifeReport.name_reading?.ai_text && (
                        <div className="member-name-ai">
                          <span>{copy.nameAiReading}</span>
                          <ReactMarkdown>{txt(lifeReport.name_reading.ai_text)}</ReactMarkdown>
                        </div>
                      )}
                    </article>
                    <article className="member-insight-card">
                      <span>{copy.recommendedJobs}</span>
                      <strong>{(lifeReport.recommended_jobs || []).map((job) => txt(job)).join('、')}</strong>
                      <p>{txt((lifeReport.sections || []).find((item) => item.key === 'career')?.summary || '')}</p>
                    </article>
                  </div>

                  <h2>{copy.recentLuck}</h2>
                  <div className="member-recent-grid">
                    {[
                      [copy.currentLuckCycle, recent.luck_pillar?.summary, recent.luck_pillar?.pillar],
                      [copy.currentYear, recent.year?.summary, scoreText(recent.year?.score)],
                      [copy.currentMonth, recent.month?.summary, scoreText(recent.month?.score)],
                      [copy.currentWeek, recent.week?.summary, scoreText(recent.week?.score)],
                      [copy.today, recent.day?.summary, scoreText(recent.day?.score)],
                    ].map(([title, summary, meta]) => (
                      <article className="member-recent-card" key={title}>
                        <span>{title}</span>
                        <strong>{txt(meta)}</strong>
                        <p>{txt(summary)}</p>
                      </article>
                    ))}
                  </div>

                  <h2>{copy.lifeSections}</h2>
                  <div className="member-domain-grid">
                    {(lifeReport.sections || []).map((section) => (
                      <article className="member-domain-card" key={section.key}>
                        <div>
                          <span>{txt(section.tone) || copy.report}</span>
                          <strong>{scoreText(section.score)}</strong>
                        </div>
                        <h3>{txt(section.title)}</h3>
                        <p>{txt(section.summary)}</p>
                        <p>{txt(section.advice)}</p>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              <div className="member-chart"><Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { min: 0, max: 100 } } }} /></div>
              <div className="member-report-copy">
                <h2>{copy.report}</h2>
                <p>{txt(report.fortune.ziping?.personality?.plain || '')}</p>
                <p>{txt(report.fortune.liangtoujian?.judgement || report.fortune.liangtoujian?.summary || '')}</p>
                <p>{txt(chengguText)}</p>
              </div>
              <h2>{copy.annualTitle}</h2>
              <div className="member-card-row">
                {years.map((year) => (
                  <article key={year.position} className="member-mini-card">
                    <strong>{year.year} · {txt(year.zodiac)}</strong>
                    <span>{year.score}/100</span>
                    <p>{txt(year.overall_narrative || year.overall || '')}</p>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {activeTab === 'calendar' && (
        <section className="member-panel">
          {busy && <p>{copy.loading}</p>}
          <div className="member-calendar">
            {(calendar?.days || []).map((day) => (
              <article key={day.date} className="member-day-card">
                <strong>{day.date}</strong>
                <span>{txt(day.pillar)} · {day.score}/100</span>
                <p><b>{copy.should}：</b>{txt(day.should_do)}</p>
                <p><b>{copy.avoid}：</b>{txt(day.avoid)}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'chat' && (
        <section className="member-panel">
          {!isActive && <p className="advisor-note">{copy.subscribeFirst}</p>}
          <div className="member-chat-log">
            {messages.map((message, index) => (
              <div key={index} className={`fc-msg ${message.role === 'user' ? 'fc-msg--user' : ''}`}>
                <span className="fc-msg__avatar" aria-hidden="true">{message.role === 'user' ? '·' : '玄'}</span>
                <div className="fc-msg__body">
                  {message.role === 'assistant' ? <ReactMarkdown>{txt(message.content)}</ReactMarkdown> : message.content}
                </div>
              </div>
            ))}
          </div>
          <div className="fc-chat__foot">
            <textarea className="fc-textarea" value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder={copy.askPlaceholder} rows="2" disabled={!isActive || busy} />
            <button type="button" className="fc-btn fc-btn--primary" onClick={sendChat} disabled={!isActive || busy || !chatInput.trim()}>{copy.send}</button>
          </div>
        </section>
      )}
    </main>
  );
};

export default MemberPage;
